import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import communityService, { CommunityMember } from '@/services/communityService';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/Button';
import { getPaginationItems } from '@/utils/pagination';
import showToast from '@/components/ui/Toast';

// Helper function to format date to Month Year (e.g. "Mar 2021")
const formatMonthYear = (dateStr?: string): string => {
  if (!dateStr) return 'Jan 2024';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
    return `${months[d.getMonth()]} ${d.getFullYear()}`;
  } catch {
    return dateStr || 'Jan 2024';
  }
};

// Helper function to format full date (e.g. "15 Mar 2024")
const formatDateFull = (dateStr?: string): string => {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  } catch {
    return dateStr || '-';
  }
};

// Helper function to get initials
const getInitials = (name?: string): string => {
  if (!name) return 'KM';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

// Helper function to format WhatsApp link
const formatWhatsAppUrl = (phone: string): string => {
  let cleaned = phone.replace(/\D/g, '');
  if (cleaned.startsWith('0')) {
    cleaned = '62' + cleaned.slice(1);
  }
  return `https://wa.me/${cleaned}`;
};

const AVATAR_COLORS = [
  'bg-blue-500',
  'bg-emerald-500',
  'bg-violet-500',
  'bg-amber-500',
  'bg-rose-500',
  'bg-cyan-500',
  'bg-indigo-500',
];

type SortField = 'keaktifan' | 'event' | 'match' | 'goal' | 'assist' | 'save';

const CommunityMembersPage: React.FC = () => {
  const navigate = useNavigate();
  const [members, setMembers] = useState<CommunityMember[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [activeCount, setActiveCount] = useState<number>(0);
  const [newThisMonth, setNewThisMonth] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [search, setSearch] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [sortBy, setSortBy] = useState<SortField>('keaktifan');
  const [loading, setLoading] = useState<boolean>(true);
  const [communitySlug, setCommunitySlug] = useState<string>('');
  const [communityName, setCommunityName] = useState<string>('');
  const [showInviteModal, setShowInviteModal] = useState<boolean>(false);
  const [selectedMember, setSelectedMember] = useState<CommunityMember | null>(null);
  const [memberDetail, setMemberDetail] = useState<any | null>(null);
  const [loadingDetail, setLoadingDetail] = useState<boolean>(false);

  const limit = 6;

  // Scroll main container to top on page change
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    document.querySelectorAll('main').forEach((container) => {
      container.scrollTo({ top: 0, behavior: 'smooth' });
    });
  };

  // Fetch community profile first to get slug & name
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const profileRes = await communityService.getProfile();
        if (profileRes?.data?.community?.slug) {
          setCommunitySlug(profileRes.data.community.slug);
          setCommunityName(profileRes.data.community.name || 'Komunitas');
        }
      } catch (err) {
        console.error('Failed to fetch community profile:', err);
      }
    };
    fetchProfile();
  }, []);

  // Fetch member stats
  const fetchMemberStats = useCallback(async () => {
    if (!communitySlug) return;
    try {
      const statsRes = await communityService.getCommunityMemberStats(communitySlug);
      if (statsRes?.data) {
        setActiveCount(statsRes.data.active_count || 0);
        setNewThisMonth(statsRes.data.new_this_month || 0);
      }
    } catch (err) {
      console.error('Failed to fetch member stats:', err);
    }
  }, [communitySlug]);

  // Fetch paginated member list
  const fetchMembers = useCallback(async () => {
    if (!communitySlug) return;
    setLoading(true);
    scrollToTop();
    try {
      const response = await communityService.getCommunityMembers(communitySlug, {
        page,
        limit,
        search: search || undefined,
        sort_by: sortBy,
      });

      if (response?.success && response.data && Array.isArray(response.data.members)) {
        setMembers(response.data.members);
        setTotalCount(response.data.total_count || response.data.members.length);
      } else {
        setMembers([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error('Failed to fetch community members:', err);
      setMembers([]);
      setTotalCount(0);
    } finally {
      setTimeout(() => setLoading(false), 200);
    }
  }, [communitySlug, page, limit, search, sortBy]);

  // Handle search submit
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput.trim());
    setPage(1);
  };

  const handleCopyInviteLink = () => {
    const inviteUrl = window.location.origin + `/community/${communitySlug || 'sporto'}/join`;
    navigator.clipboard.writeText(inviteUrl);
    showToast.success('Tautan undang anggota berhasil disalin!');
  };

  // Debounced search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchMemberStats();
  }, [fetchMemberStats]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  useEffect(() => {
    const handleInviteTrigger = () => setShowInviteModal(true);
    window.addEventListener('sporto-trigger-invite-member', handleInviteTrigger);
    return () => window.removeEventListener('sporto-trigger-invite-member', handleInviteTrigger);
  }, []);

  const totalPages = Math.ceil(totalCount / limit) || 1;
  const startItem = totalCount === 0 ? 0 : (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, totalCount);

  return (
    <div className="min-h-screen bg-gray-50/60 pb-20 md:pb-8">
      {/* Main Content Area (Mobile/Tablet) */}
      <div className="lg:hidden max-w-7xl mx-auto px-4 md:px-6 pt-3 md:pt-6 space-y-4 md:space-y-6">
        {/* 2 Stat Cards: Aktif & Baru Bulan Ini */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white rounded-2xl p-4 border border-gray-150 shadow-xs flex flex-col gap-1">
            <span className="text-xs font-semibold text-gray-500">Aktif</span>
            <span className="text-xl font-extrabold text-[#0E1726]">
              {new Intl.NumberFormat('id-ID').format(activeCount || totalCount || 0)}
            </span>
          </div>

          <div className="bg-white rounded-2xl p-4 border border-gray-150 shadow-xs flex flex-col gap-1">
            <span className="text-xs font-semibold text-gray-500">Baru bulan ini</span>
            <span className="text-xl font-extrabold text-[#10B981]">
              +{new Intl.NumberFormat('id-ID').format(newThisMonth || 0)}
            </span>
          </div>
        </div>

        {/* Search Bar */}
        <div>
          <form onSubmit={handleSearchSubmit} className="relative">
            <Icon
              icon="ph:magnifying-glass-bold"
              className="absolute left-4 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-gray-400"
            />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Cari anggota..."
              className="w-full pl-11 pr-9 py-3 bg-white border border-gray-200 rounded-2xl text-xs font-medium focus:outline-none focus:border-[#FC480D] focus:ring-1 focus:ring-[#FC480D] transition-all shadow-2xs placeholder:text-gray-400"
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => {
                  setSearchInput('');
                  setSearch('');
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
              >
                <Icon icon="ph:x-circle-fill" className="w-4 h-4" />
              </button>
            )}
          </form>
        </div>

        {/* Sort By Filters (Urutkan) */}
        <div className="mt-3.5 flex items-center gap-2 overflow-x-auto no-scrollbar pb-1">
          <div className="flex items-center gap-1 text-xs text-gray-400 font-medium mr-1 flex-shrink-0">
            <Icon icon="ph:arrows-down-up-bold" className="w-3.5 h-3.5" />
            <span>Urutkan</span>
          </div>

          <button
            onClick={() => { setSortBy('event'); setPage(1); }}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${sortBy === 'event' ? 'bg-[#FC480D] text-white shadow-xs' : 'bg-white text-gray-700 border border-gray-200'}`}
          >Event</button>
          <button
            onClick={() => { setSortBy('match'); setPage(1); }}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${sortBy === 'match' ? 'bg-[#FC480D] text-white shadow-xs' : 'bg-white text-gray-700 border border-gray-200'}`}
          >Match</button>
          <button
            onClick={() => { setSortBy('keaktifan'); setPage(1); }}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${sortBy === 'keaktifan' ? 'bg-[#FC480D] text-white shadow-xs' : 'bg-white text-gray-700 border border-gray-200'}`}
          >Keaktifan</button>
          <button
            onClick={() => { setSortBy('goal'); setPage(1); }}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${sortBy === 'goal' ? 'bg-[#FC480D] text-white shadow-xs' : 'bg-white text-gray-700 border border-gray-200'}`}
          >Gol</button>
          <button
            onClick={() => { setSortBy('assist'); setPage(1); }}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${sortBy === 'assist' ? 'bg-[#FC480D] text-white shadow-xs' : 'bg-white text-gray-700 border border-gray-200'}`}
          >Assist</button>
          <button
            onClick={() => { setSortBy('save'); setPage(1); }}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${sortBy === 'save' ? 'bg-[#FC480D] text-white shadow-xs' : 'bg-white text-gray-700 border border-gray-200'}`}
          >Save</button>
        </div>

        {/* Member Table Card Container */}
        <div className="mt-3.5 bg-white rounded-2xl border border-gray-150 shadow-xs overflow-hidden">
          {/* Table Header */}
          <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr] items-center px-4 py-3 bg-white border-b border-gray-100 text-[10.5px] font-bold text-gray-400 uppercase tracking-wider">
            <div>ANGGOTA</div>
            <div className={`text-center ${sortBy === 'event' ? 'text-[#FC480D]' : ''}`}>EVENT</div>
            <div className={`text-center ${sortBy === 'match' ? 'text-[#FC480D]' : ''}`}>MATCH</div>
            <div className={`text-center ${['keaktifan', 'goal', 'assist', 'save'].includes(sortBy) ? 'text-[#FC480D]' : ''}`}>
              {sortBy === 'goal' ? 'GOL' : sortBy === 'assist' ? 'ASSIST' : sortBy === 'save' ? 'SAVE' : 'KEAKTIFAN'}
            </div>
          </div>

          {/* Skeleton Loading Rows */}
          {loading ? (
            <div className="divide-y divide-gray-100">
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <div key={n} className="grid grid-cols-[1.4fr_1fr_1fr_1fr] items-center px-4 py-3.5 animate-pulse">
                  <div className="flex items-center gap-2.5">
                    <div className="w-10 h-10 bg-gray-200 rounded-full flex-shrink-0" />
                    <div className="space-y-1.5 flex-1">
                      <div className="h-3.5 bg-gray-200 rounded w-24" />
                      <div className="h-2.5 bg-gray-150 rounded w-14" />
                    </div>
                  </div>
                  <div className="h-4 bg-gray-200 rounded w-8 mx-auto" />
                  <div className="h-4 bg-gray-200 rounded w-8 mx-auto" />
                  <div className="h-4 bg-gray-200 rounded w-10 mx-auto" />
                </div>
              ))}
            </div>
          ) : members.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <div className="w-12 h-12 rounded-full bg-orange-50 text-[#FC480D] flex items-center justify-center mx-auto mb-3">
                <Icon icon="ph:users-three-bold" className="w-6 h-6" />
              </div>
              <p className="font-bold text-gray-800 text-sm">Tidak ada anggota</p>
              <p className="text-xs text-gray-400 mt-1">Coba kata kunci pencarian yang lain.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {members.map((item, idx) => {
                const userName = item.community_user?.name || item.community_user?.nickname || item.community_user?.username || 'Anggota';
                const username = item.community_user?.username;
                const avatarUrl = item.community_user?.avatar;
                const colorBg = AVATAR_COLORS[idx % AVATAR_COLORS.length];
                const eventCount = item.event_count ?? 0;
                const matchCount = item.match_count ?? 0;
                const keaktifan = item.keaktifan_percentage ?? 0;
                const goalCount = item.goal_count ?? item.gol_count ?? 0;
                const assistCount = item.assist_count ?? 0;
                const saveCount = item.save_count ?? 0;

                const handleMemberClick = () => {
                  setSelectedMember(item);
                  setMemberDetail(null);
                  setLoadingDetail(true);
                  if (communitySlug && item.id) {
                    communityService
                      .getCommunityMemberDetail(communitySlug, item.id)
                      .then((res) => {
                        if (res?.data) {
                          setMemberDetail(res.data);
                        }
                      })
                      .catch((err) => {
                        console.error('Failed to fetch member detail:', err);
                      })
                      .finally(() => {
                        setLoadingDetail(false);
                      });
                  } else {
                    setLoadingDetail(false);
                  }
                };

                return (
                  <div
                    key={item.id || idx}
                    onClick={handleMemberClick}
                    className={`grid grid-cols-[1.4fr_1fr_1fr_1fr] items-center px-4 py-3.5 transition-colors ${
                      username ? 'cursor-pointer hover:bg-orange-50/60' : 'hover:bg-gray-50/60'
                    }`}
                  >
                    {/* User Info Column */}
                    <div className="flex items-center gap-2.5 min-w-0 pr-1">
                      {avatarUrl ? (
                        <img
                          src={avatarUrl}
                          alt={userName}
                          className="w-10 h-10 rounded-full object-cover border border-gray-100 flex-shrink-0"
                        />
                      ) : (
                        <div
                          className={`w-10 h-10 rounded-full ${colorBg} text-white font-bold text-xs flex items-center justify-center flex-shrink-0 shadow-xs`}
                        >
                          {getInitials(userName)}
                        </div>
                      )}
                      <div className="min-w-0">
                        <h4 className="font-bold text-gray-900 text-sm truncate leading-tight hover:text-[#FC480D] transition-colors">
                          {userName}
                        </h4>
                        <span className="text-[11px] text-gray-400 block mt-0.5 font-medium">
                          {formatMonthYear(item.join_date)}
                        </span>
                      </div>
                    </div>

                    {/* Event Count */}
                    <div className="text-center font-bold text-gray-900 text-sm">
                      {eventCount}
                    </div>

                    {/* Match Count */}
                    <div className="text-center font-bold text-gray-900 text-sm">
                      {matchCount}
                    </div>

                    {/* Dynamic Metric Column (Keaktifan / Gol / Assist / Save) */}
                    <div className="text-center font-bold text-sm">
                      {sortBy === 'goal' ? (
                        <span className="text-[#FC480D] font-extrabold">{goalCount}</span>
                      ) : sortBy === 'assist' ? (
                        <span className="text-[#FC480D] font-extrabold">{assistCount}</span>
                      ) : sortBy === 'save' ? (
                        <span className="text-[#FC480D] font-extrabold">{saveCount}</span>
                      ) : (
                        <span className="text-[#FC480D] font-bold">{keaktifan}%</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Pagination Controls Mobile */}
        <div className="mx-4 mt-4 flex items-center justify-between">
          <span className="text-xs text-gray-400 font-medium">
            {startItem}–{endItem} dari {totalCount} anggota
          </span>

          <div className="flex items-center gap-1.5">
            <button
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="w-8 h-8 rounded-full border border-gray-200 bg-white flex items-center justify-center text-gray-500 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
            >
              <Icon icon="ph:caret-left-bold" className="w-4 h-4" />
            </button>

            {getPaginationItems(page, totalPages).map((item, idx) => {
              if (item === '...') {
                return (
                  <span key={`ellipsis-m-${idx}`} className="w-6 h-8 flex items-center justify-center text-xs text-gray-400 font-bold select-none shrink-0">
                    ...
                  </span>
                );
              }
              const pNum = item as number;
              return (
                <button
                  key={pNum}
                  onClick={() => setPage(pNum)}
                  className={`w-8 h-8 rounded-xl font-bold text-xs flex items-center justify-center transition-all shrink-0 ${
                    page === pNum
                      ? 'bg-[#FC480D] text-white shadow-xs'
                      : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {pNum}
                </button>
              );
            })}

            <button
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="w-8 h-8 rounded-full border border-gray-200 bg-white flex items-center justify-center text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
            >
              <Icon icon="ph:caret-right-bold" className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* 💻 DESKTOP WEB APP VIEW (Layar Desktop / Tablet Wide) */}
      {/* ========================================================================= */}
      <div className="hidden lg:block space-y-6 max-w-7xl mx-auto px-6 pt-4">
        {/* Desktop Header Banner */}
        <div className="flex items-center justify-between bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">
                Member Komunitas
              </h1>
              <Badge className="bg-orange-50 text-[#FC480D] border-orange-200 text-xs px-3 py-1 font-semibold">
                {activeCount.toLocaleString('id-ID')} Anggota Aktif
              </Badge>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Pantau keaktifan, statistik match, dan daftar anggota komunitas {communityName}.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Button
              onClick={() => setShowInviteModal(true)}
              className="bg-[#FC480D] hover:bg-[#D83B08] text-white font-semibold rounded-xl px-4 py-2 text-sm shadow-xs flex items-center gap-2"
            >
              <Icon icon="ph:user-plus-bold" className="w-4 h-4" />
              <span>Undang Anggota</span>
            </Button>
          </div>
        </div>

        {/* 3 Metric Cards Desktop */}
        <div className="grid grid-cols-3 gap-5">
          <Card className="bg-white p-5 rounded-2xl border border-gray-200 shadow-xs">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Anggota Aktif</span>
            <div className="flex items-baseline justify-between mt-2">
              <span className="text-2xl font-black text-gray-900">{activeCount.toLocaleString('id-ID')}</span>
              <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">Aktif</span>
            </div>
          </Card>

          <Card className="bg-white p-5 rounded-2xl border border-gray-200 shadow-xs">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Baru Bulan Ini</span>
            <div className="flex items-baseline justify-between mt-2">
              <span className="text-2xl font-black text-[#00B050]">+{newThisMonth}</span>
              <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">Bulan Ini</span>
            </div>
          </Card>

          <Card className="bg-white p-5 rounded-2xl border border-gray-200 shadow-xs">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Terdaftar</span>
            <div className="flex items-baseline justify-between mt-2">
              <span className="text-2xl font-black text-gray-900">{totalCount}</span>
              <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">Komunitas</span>
            </div>
          </Card>
        </div>

        {/* Filter Toolbar Desktop */}
        <div className="flex items-center justify-between bg-white p-4 rounded-2xl border border-gray-200 shadow-xs">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider mr-2">Urutkan:</span>
            {(['keaktifan', 'event', 'match'] as SortField[]).map((field) => (
              <button
                key={field}
                onClick={() => {
                  setSortBy(field);
                  setPage(1);
                }}
                className={`px-4 py-1.5 rounded-xl text-xs font-bold capitalize transition-all ${
                  sortBy === field
                    ? 'bg-[#FC480D] text-white shadow-xs'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {field}
              </button>
            ))}
          </div>

          <div className="relative w-72">
            <Icon
              icon="ph:magnifying-glass-bold"
              className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Cari nama anggota..."
              className="w-full pl-10 pr-8 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:bg-white focus:outline-none focus:border-[#FC480D] focus:ring-1 focus:ring-[#FC480D] transition-all"
            />
            {searchInput && (
              <button
                onClick={() => setSearchInput('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <Icon icon="ph:x-circle-fill" className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Data Table Desktop View */}
        <Card className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-xs">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-xs font-bold text-gray-500 uppercase tracking-wider">
                  <th className="py-4 px-6">Anggota</th>
                  <th className="py-4 px-6 text-center">Bergabung</th>
                  <th className={`py-4 px-6 text-center ${sortBy === 'event' ? 'text-[#FC480D]' : ''}`}>Total Event</th>
                  <th className={`py-4 px-6 text-center ${sortBy === 'match' ? 'text-[#FC480D]' : ''}`}>Total Match</th>
                  <th className={`py-4 px-6 text-center ${['keaktifan', 'goal', 'assist', 'save'].includes(sortBy) ? 'text-[#FC480D]' : ''}`}>
                    {sortBy === 'goal' ? 'Total Gol' : sortBy === 'assist' ? 'Total Assist' : sortBy === 'save' ? 'Total Save' : 'Keaktifan'}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-150">
                {loading ? (
                  [1, 2, 3, 4, 5, 6].map((n) => (
                    <tr key={n} className="animate-pulse">
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gray-200 rounded-full" />
                          <div className="space-y-1.5">
                            <div className="h-4 bg-gray-200 rounded w-36" />
                            <div className="h-3 bg-gray-150 rounded w-24" />
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6"><div className="h-4 bg-gray-200 rounded w-20 mx-auto" /></td>
                      <td className="py-4 px-6"><div className="h-4 bg-gray-200 rounded w-12 mx-auto" /></td>
                      <td className="py-4 px-6"><div className="h-4 bg-gray-200 rounded w-12 mx-auto" /></td>
                      <td className="py-4 px-6"><div className="h-4 bg-gray-200 rounded w-16 mx-auto" /></td>
                    </tr>
                  ))
                ) : members.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-12 text-center text-gray-500">
                      <div className="w-12 h-12 rounded-full bg-orange-50 text-[#FC480D] flex items-center justify-center mx-auto mb-3">
                        <Icon icon="ph:users-three-bold" className="w-6 h-6" />
                      </div>
                      <p className="font-bold text-gray-800 text-sm">Tidak ada anggota yang ditemukan</p>
                      <p className="text-xs text-gray-400 mt-1">Coba kata kunci pencarian yang lain.</p>
                    </td>
                  </tr>
                ) : (
                  members.map((item, idx) => {
                    const userName = item.community_user?.name || item.community_user?.nickname || item.community_user?.username || 'Anggota';
                    const username = item.community_user?.username;
                    const avatarUrl = item.community_user?.avatar;
                    const colorBg = AVATAR_COLORS[idx % AVATAR_COLORS.length];
                    const eventCount = item.event_count ?? 0;
                    const matchCount = item.match_count ?? 0;
                    const keaktifan = item.keaktifan_percentage ?? 0;
                    const goalCount = item.goal_count ?? item.gol_count ?? 0;
                    const assistCount = item.assist_count ?? 0;
                    const saveCount = item.save_count ?? 0;

                    const handleMemberClick = () => {
                      if (username) {
                        navigate(`/profile/${username}`);
                      }
                    };

                    return (
                      <tr
                        key={item.id || idx}
                        onClick={handleMemberClick}
                        className={`transition-colors ${
                          username ? 'cursor-pointer hover:bg-orange-50/50' : 'hover:bg-gray-50/80'
                        }`}
                      >
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-3">
                            {avatarUrl ? (
                              <img
                                src={avatarUrl}
                                alt={userName}
                                className="w-10 h-10 rounded-full object-cover border border-gray-200"
                              />
                            ) : (
                              <div
                                className={`w-10 h-10 rounded-full ${colorBg} text-white font-bold text-xs flex items-center justify-center shadow-xs`}
                              >
                                {getInitials(userName)}
                              </div>
                            )}
                            <div>
                              <span className="font-bold text-gray-900 text-sm block hover:text-[#FC480D] transition-colors">
                                {userName}
                              </span>
                              {username && (
                                <span className="text-xs text-gray-400 block mt-0.5">
                                  @{username}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>

                        <td className="py-4 px-6 text-center text-xs font-semibold text-gray-600">
                          {formatDateFull(item.join_date)}
                        </td>

                        <td className="py-4 px-6 text-center text-sm font-bold text-gray-900">
                          {eventCount}
                        </td>

                        <td className="py-4 px-6 text-center text-sm font-bold text-gray-900">
                          {matchCount}
                        </td>

                        <td className="py-4 px-6 text-center">
                          {sortBy === 'goal' ? (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-black bg-emerald-50 text-emerald-600">
                              {goalCount}
                            </span>
                          ) : sortBy === 'assist' ? (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-black bg-blue-50 text-blue-600">
                              {assistCount}
                            </span>
                          ) : sortBy === 'save' ? (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-black bg-purple-50 text-purple-600">
                              {saveCount}
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-black bg-orange-50 text-[#FC480D]">
                              {keaktifan}%
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Desktop Pagination Footer */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
              <span className="text-xs font-semibold text-gray-500">
                Menampilkan {startItem}–{endItem} dari {totalCount} anggota
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="rounded-xl text-xs bg-white"
                >
                  <Icon icon="ph:caret-left-bold" className="w-3.5 h-3.5 mr-1" /> Sebelum
                </Button>

                {getPaginationItems(page, totalPages).map((item, idx) => {
                  if (item === '...') {
                    return (
                      <span key={`ellipsis-d-${idx}`} className="w-6 h-8 flex items-center justify-center text-xs text-gray-400 font-bold select-none shrink-0">
                        ...
                      </span>
                    );
                  }
                  const pNum = item as number;
                  return (
                    <button
                      key={pNum}
                      onClick={() => setPage(pNum)}
                      className={`w-8 h-8 rounded-xl font-bold text-xs flex items-center justify-center transition-all shrink-0 ${
                        page === pNum
                          ? 'bg-[#FC480D] text-white shadow-xs'
                          : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {pNum}
                    </button>
                  );
                })}

                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages || loading}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="rounded-xl text-xs bg-white"
                >
                  Selanjutnya <Icon icon="ph:caret-right-bold" className="w-3.5 h-3.5 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* ========================================================================= */}
      {/* 🚀 MODAL UNDANG ANGGOTA */}
      {/* ========================================================================= */}
      {showInviteModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 animate-in fade-in duration-200"
          onClick={() => setShowInviteModal(false)}
        >
          <div
            className="bg-white rounded-3xl p-6 w-full max-w-sm space-y-4 shadow-xl border border-gray-100"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b pb-3">
              <h3 className="font-bold text-gray-900 text-base flex items-center gap-2">
                <Icon icon="ph:user-plus-bold" className="text-[#FC480D] w-5 h-5" />
                Undang Anggota
              </h3>
              <button
                onClick={() => setShowInviteModal(false)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <Icon icon="ph:x-bold" className="w-5 h-5" />
              </button>
            </div>

            <p className="text-xs text-gray-500 leading-relaxed">
              Bagikan tautan berikut untuk mengajak anggota baru bergabung ke komunitas {communityName}.
            </p>

            <div className="bg-gray-50 p-3 rounded-2xl border border-gray-200 flex items-center justify-between gap-2">
              <span className="text-xs font-mono text-gray-700 truncate flex-1">
                {window.location.origin}/community/{communitySlug || 'sporto'}/join
              </span>
              <button
                onClick={handleCopyInviteLink}
                className="bg-[#FC480D] hover:bg-[#D83B08] text-white text-xs font-semibold px-3 py-1.5 rounded-xl transition-colors flex-shrink-0 flex items-center gap-1"
              >
                <Icon icon="ph:copy-bold" className="w-3.5 h-3.5" />
                <span>Salin</span>
              </button>
            </div>

            <Button
              onClick={() => setShowInviteModal(false)}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl py-2.5 text-xs transition-colors"
            >
              Tutup
            </Button>
          </div>
        </div>
      )}

      {/* Member Details Bottom Sheet Modal */}
      {selectedMember && (
        <div
          className="fixed inset-0 z-50 bg-[#0E1726]/60 backdrop-blur-xs flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-200"
          onClick={() => {
            setSelectedMember(null);
            setMemberDetail(null);
          }}
        >
          <div
            className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-5 pb-6 flex flex-col gap-4 shadow-2xl animate-in slide-in-from-bottom duration-300 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Handle Bar */}
            <div className="w-12 h-1.5 rounded-full bg-gray-300 self-center shrink-0 mb-1" />

            {loadingDetail ? (
              /* Skeleton Loading state */
              <div className="flex flex-col gap-4 animate-pulse">
                {/* Header Skeleton */}
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-14 h-14 rounded-full bg-gray-200 shrink-0" />
                    <div className="space-y-2 flex-1">
                      <div className="h-4 bg-gray-200 rounded w-36" />
                      <div className="h-3 bg-gray-150 rounded w-24" />
                    </div>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-gray-100 shrink-0" />
                </div>

                {/* 3 Mini Stat Box Skeleton */}
                <div className="bg-gray-50 border border-gray-100 rounded-2xl p-4 grid grid-cols-3 gap-2">
                  <div className="h-10 bg-gray-200 rounded-xl" />
                  <div className="h-10 bg-gray-200 rounded-xl" />
                  <div className="h-10 bg-gray-200 rounded-xl" />
                </div>

                {/* Detail List Box Skeleton */}
                <div className="bg-white rounded-2xl border border-gray-200 p-3 space-y-3">
                  <div className="h-9 bg-gray-100 rounded-xl" />
                  <div className="h-9 bg-gray-100 rounded-xl" />
                  <div className="h-9 bg-gray-100 rounded-xl" />
                  <div className="h-9 bg-gray-100 rounded-xl" />
                </div>

                {/* Action Buttons Skeleton */}
                <div className="grid grid-cols-2 gap-3 pt-1">
                  <div className="h-12 bg-gray-200 rounded-full" />
                  <div className="h-12 bg-gray-200 rounded-full" />
                </div>
              </div>
            ) : (
              /* Loaded Content */
              <>
                {/* Header Info */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {(memberDetail?.avatar || selectedMember.community_user?.avatar) ? (
                      <img
                        src={memberDetail?.avatar || selectedMember.community_user?.avatar}
                        alt={memberDetail?.name || selectedMember.community_user?.name || 'Member'}
                        className="w-14 h-14 rounded-full object-cover shrink-0 border border-gray-100 shadow-2xs"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-[#FC480D] text-white font-bold text-lg flex items-center justify-center shrink-0 shadow-2xs">
                        {getInitials(
                          memberDetail?.name ||
                            selectedMember.community_user?.name ||
                            selectedMember.community_user?.nickname
                        )}
                      </div>
                    )}

                    <div className="flex flex-col min-w-0">
                      <h3 className="text-base sm:text-lg font-extrabold text-[#0E1726] truncate leading-snug">
                        {memberDetail?.name ||
                          selectedMember.community_user?.name ||
                          selectedMember.community_user?.nickname ||
                          'Anggota'}
                      </h3>
                      <span className="text-xs text-gray-400 font-medium truncate">
                        Gabung {formatMonthYear(memberDetail?.join_date || selectedMember.join_date || selectedMember.created_at)}
                      </span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setSelectedMember(null);
                      setMemberDetail(null);
                    }}
                    className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 flex items-center justify-center transition-colors cursor-pointer shrink-0"
                  >
                    <Icon icon="ph:x-bold" className="w-4 h-4" />
                  </button>
                </div>

                {/* 3 Mini Stat Box Grid */}
                <div className="bg-gray-50/80 border border-gray-100 rounded-2xl p-3.5 grid grid-cols-3 divide-x divide-gray-200/80 text-center">
                  <div className="flex flex-col items-center justify-center px-1">
                    <span className="text-lg font-extrabold text-[#0E1726]">
                      {memberDetail?.event_count ?? selectedMember.event_count ?? 0}
                    </span>
                    <span className="text-[11px] font-medium text-gray-400 mt-0.5">Event ikut</span>
                  </div>

                  <div className="flex flex-col items-center justify-center px-1">
                    <span className="text-lg font-extrabold text-[#0E1726]">
                      {memberDetail?.keaktifan_percentage ?? selectedMember.keaktifan_percentage ?? 0}%
                    </span>
                    <span className="text-[11px] font-medium text-gray-400 mt-0.5">Keaktifan</span>
                  </div>

                  <div className="flex flex-col items-center justify-center px-1">
                    <span className="text-lg font-extrabold text-[#0E1726]">
                      {memberDetail?.win_rate ?? (selectedMember as any).win_rate ?? 0}%
                    </span>
                    <span className="text-[11px] font-medium text-gray-400 mt-0.5">Winrate</span>
                  </div>
                </div>

                {/* Detail List Box */}
                <div className="bg-white rounded-2xl border border-gray-200/80 overflow-hidden divide-y divide-gray-100">
                  {/* NOMOR HP */}
                  <div className="p-3.5 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-xl bg-gray-50 text-gray-400 flex items-center justify-center shrink-0 border border-gray-100">
                      <Icon icon="ph:phone-bold" className="w-4 h-4" />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="text-[10px] font-bold text-gray-400 tracking-wider uppercase">NOMOR HP</span>
                      <span className="text-xs font-bold text-[#0E1726] truncate">
                        {memberDetail?.phone || '-'}
                      </span>
                    </div>
                  </div>

                  {/* EMAIL */}
                  <div className="p-3.5 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-xl bg-gray-50 text-gray-400 flex items-center justify-center shrink-0 border border-gray-100">
                      <Icon icon="ph:envelope-simple-bold" className="w-4 h-4" />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="text-[10px] font-bold text-gray-400 tracking-wider uppercase">EMAIL</span>
                      <span className="text-xs font-bold text-[#0E1726] truncate">
                        {memberDetail?.email || '-'}
                      </span>
                    </div>
                  </div>

                  {/* BERGABUNG */}
                  <div className="p-3.5 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-xl bg-gray-50 text-gray-400 flex items-center justify-center shrink-0 border border-gray-100">
                      <Icon icon="ph:calendar-blank-bold" className="w-4 h-4" />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="text-[10px] font-bold text-gray-400 tracking-wider uppercase">BERGABUNG</span>
                      <span className="text-xs font-bold text-[#0E1726] truncate">
                        {formatMonthYear(memberDetail?.join_date || selectedMember.join_date || selectedMember.created_at)}
                      </span>
                    </div>
                  </div>

                  {/* TERAKHIR IKUT EVENT */}
                  <div className="p-3.5 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-xl bg-gray-50 text-gray-400 flex items-center justify-center shrink-0 border border-gray-100">
                      <Icon icon="ph:clock-counter-clockwise-bold" className="w-4 h-4" />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="text-[10px] font-bold text-gray-400 tracking-wider uppercase">TERAKHIR IKUT EVENT</span>
                      <span className="text-xs font-bold text-[#0E1726] truncate">
                        {memberDetail?.last_event_name
                          ? `${formatDateFull(memberDetail.last_event_date)} · ${memberDetail.last_event_name}`
                          : '-'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* 2 Bottom Action Buttons */}
                <div className="grid grid-cols-2 gap-3 pt-1">
                  {/* Lihat Profile */}
                  <button
                    type="button"
                    onClick={() => {
                      const userSlug =
                        memberDetail?.username ||
                        selectedMember.community_user?.username ||
                        selectedMember.community_user?.nickname;
                      setSelectedMember(null);
                      setMemberDetail(null);
                      if (userSlug) {
                        navigate(`/profile/${userSlug}`);
                      } else {
                        showToast.error('Profil pemain tidak tersedia');
                      }
                    }}
                    className="h-12 rounded-full border border-gray-300 text-gray-800 text-xs font-bold flex items-center justify-center gap-2 hover:bg-gray-50 active:scale-98 transition-all cursor-pointer"
                  >
                    <Icon icon="ph:user-bold" className="w-4 h-4 text-gray-700" />
                    <span>Lihat Profile</span>
                  </button>

                  {/* Hubungi */}
                  {memberDetail?.phone ? (
                    <a
                      href={formatWhatsAppUrl(memberDetail.phone)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="h-12 rounded-full bg-[#FC480D] hover:bg-[#e03d07] text-white text-xs font-bold flex items-center justify-center gap-2 active:scale-98 transition-all cursor-pointer shadow-sm"
                    >
                      <Icon icon="ph:whatsapp-logo-fill" className="w-4.5 h-4.5 text-white" />
                      <span>Hubungi</span>
                    </a>
                  ) : memberDetail?.email ? (
                    <a
                      href={`mailto:${memberDetail.email}`}
                      className="h-12 rounded-full bg-[#FC480D] hover:bg-[#e03d07] text-white text-xs font-bold flex items-center justify-center gap-2 active:scale-98 transition-all cursor-pointer shadow-sm"
                    >
                      <Icon icon="ph:envelope-simple-fill" className="w-4.5 h-4.5 text-white" />
                      <span>Hubungi</span>
                    </a>
                  ) : (
                    <button
                      type="button"
                      disabled
                      className="h-12 rounded-full bg-gray-200 text-gray-400 text-xs font-bold flex items-center justify-center gap-2 cursor-not-allowed"
                    >
                      <Icon icon="ph:chats-teardrop-bold" className="w-4.5 h-4.5" />
                      <span>Hubungi</span>
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunityMembersPage;
